// frontend/src/pages/CamDoBrainCalendar.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainCalendar.css';

const CamDoBrainCalendar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandProfileIdParam = searchParams.get('brand_profile_id');

  // State
  const [loading, setLoading] = useState(true);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // month, week
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load calendar when brand or date changes
  useEffect(() => {
    if (selectedBrandId) {
      loadCalendarData(selectedBrandId);
    }
  }, [selectedBrandId, currentDate]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const profilesRes = await camdobrainService.getBrandProfiles();
      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data
        : (profilesRes.data?.results || []);
      setBrandProfiles(profiles);

      if (brandProfileIdParam && profiles.find(p => p.id === parseInt(brandProfileIdParam))) {
        setSelectedBrandId(parseInt(brandProfileIdParam));
      } else if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendarData = async (brandId) => {
    try {
      const startDate = getMonthStart(currentDate);
      const endDate = getMonthEnd(currentDate);

      const res = await camdobrainService.getCalendarData(brandId, startDate, endDate);
      setCalendarData(res.data);
      setError(null);
    } catch (err) {
      console.error('Error loading calendar:', err);
      setCalendarData(null);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await camdobrainService.syncCalendar(selectedBrandId);
      loadCalendarData(selectedBrandId);
    } catch (err) {
      console.error('Error syncing:', err);
      setError('Error al sincronizar calendario');
    } finally {
      setSyncing(false);
    }
  };

  const getMonthStart = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    return d.toISOString().split('T')[0];
  };

  const getMonthEnd = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return d.toISOString().split('T')[0];
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }

    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const getEntriesForDate = (date) => {
    if (!calendarData?.entries) return [];
    const dateStr = date.toISOString().split('T')[0];
    // entries es un objeto con fechas como claves, no un array
    return calendarData.entries[dateStr] || [];
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const getEntryTypeIcon = (entryType) => {
    const icons = {
      'scheduled_post': '📅',
      'suggested_content': '💡',
      'predicted_optimal': '🤖',
      'event': '🎉',
      'deadline': '⏰',
    };
    return icons[entryType] || '📌';
  };

  const getEntryTypeColor = (entryType) => {
    const colors = {
      'scheduled_post': '#3b82f6',
      'suggested_content': '#8b5cf6',
      'predicted_optimal': '#10b981',
      'event': '#f59e0b',
      'deadline': '#ef4444',
    };
    return colors[entryType] || '#64748b';
  };

  const getEntryTypeLabel = (entryType) => {
    const labels = {
      'scheduled_post': 'Post Programado',
      'suggested_content': 'Sugerencia',
      'predicted_optimal': 'Prediccion IA',
      'event': 'Evento',
      'deadline': 'Deadline',
    };
    return labels[entryType] || entryType;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('es-CL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];

  if (loading) {
    return <Loading />;
  }

  const days = getDaysInMonth(currentDate);

  return (
    <div className="calendar-page">
      {/* Header */}
      <div className="calendar-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/camdobrain')}>
            ← Volver
          </button>
          <h1>
            <span className="icon">📅</span>
            Calendario de Contenido
          </h1>
        </div>
        <div className="header-actions">
          <select
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(Number(e.target.value))}
            className="brand-select"
          >
            {brandProfiles.map(brand => (
              <option key={brand.id} value={brand.id}>
                {brand.brand_name}
              </option>
            ))}
          </select>
          <button
            className="sync-btn"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? '...' : '🔄 Sincronizar'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="error-banner">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Summary Cards */}
      {calendarData?.summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="summary-icon">📅</div>
            <div className="summary-content">
              <span className="summary-value">{calendarData.summary.scheduled_posts || 0}</span>
              <span className="summary-label">Posts Programados</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">💡</div>
            <div className="summary-content">
              <span className="summary-value">{calendarData.summary.suggestions || 0}</span>
              <span className="summary-label">Sugerencias</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🤖</div>
            <div className="summary-content">
              <span className="summary-value">{calendarData.summary.predictions || 0}</span>
              <span className="summary-label">Predicciones IA</span>
            </div>
          </div>
          <div className="summary-card">
            <div className="summary-icon">🎉</div>
            <div className="summary-content">
              <span className="summary-value">{calendarData.summary.events || 0}</span>
              <span className="summary-label">Eventos</span>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Navigation */}
      <div className="calendar-nav">
        <button className="nav-btn" onClick={() => navigateMonth(-1)}>
          ← Anterior
        </button>
        <h2>
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h2>
        <button className="estado-btn" onClick={() => navigateMonth(1)}>
          Siguiente →
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="calendar-container">
        <div className="calendar-grid">
          {/* Day Headers */}
          {dayNames.map(day => (
            <div key={day} className="day-header">
              {day}
            </div>
          ))}

          {/* Day Cells */}
          {days.map((day, idx) => {
            const entries = getEntriesForDate(day.date);
            return (
              <div
                key={idx}
                className={`day-cell ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday(day.date) ? 'today' : ''}`}
              >
                <div className="day-number">{day.date.getDate()}</div>
                <div className="day-entries">
                  {entries.slice(0, 3).map((entry, entryIdx) => (
                    <div
                      key={entryIdx}
                      className="entry-chip"
                      style={{ background: `${getEntryTypeColor(entry.entry_type)}20`, borderColor: getEntryTypeColor(entry.entry_type) }}
                      onClick={() => setSelectedEntry(entry)}
                    >
                      <span className="entry-icon">{getEntryTypeIcon(entry.entry_type)}</span>
                      <span className="entry-title">{entry.title}</span>
                    </div>
                  ))}
                  {entries.length > 3 && (
                    <div className="more-entries">
                      +{entries.length - 3} mas
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      {calendarData?.recommendations?.length > 0 && (
        <div className="recommendations-section">
          <h3>Recomendaciones IA</h3>
          <div className="recommendations-list">
            {calendarData.recommendations.map((rec, idx) => (
              <div key={idx} className={`recommendation-card priority-${rec.priority || 'medium'}`}>
                <div className="rec-icon">💡</div>
                <div className="rec-content">
                  <strong>{rec.title}</strong>
                  {rec.description && <p>{rec.description}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Entry Modal */}
      {selectedEntry && (
        <div className="entry-modal-overlay" onClick={() => setSelectedEntry(null)}>
          <div className="entry-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setSelectedEntry(null)}>×</button>
            <div className="modal-header">
              <span
                className="modal-type-badge"
                style={{ background: getEntryTypeColor(selectedEntry.entry_type) }}
              >
                {getEntryTypeIcon(selectedEntry.entry_type)} {getEntryTypeLabel(selectedEntry.entry_type)}
              </span>
            </div>
            <h3>{selectedEntry.title}</h3>
            {selectedEntry.description && (
              <p className="modal-description">{selectedEntry.description}</p>
            )}
            <div className="modal-details">
              {selectedEntry.scheduled_date && (
                <div className="detail-row">
                  <span className="detail-label">Fecha:</span>
                  <span>{new Date(selectedEntry.scheduled_date).toLocaleDateString('es-CL', {
                    weekday: 'long',
                    day: 'numeric',
                    month: 'long',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}</span>
                </div>
              )}
              {selectedEntry.platforms?.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Plataformas:</span>
                  <span>{selectedEntry.platforms.join(', ')}</span>
                </div>
              )}
              {selectedEntry.ai_confidence && (
                <div className="detail-row">
                  <span className="detail-label">Confianza IA:</span>
                  <span>{selectedEntry.ai_confidence}%</span>
                </div>
              )}
            </div>
            {selectedEntry.entry_type === 'suggested_content' && (
              <div className="modal-actions">
                <button
                  className="action-btn primary"
                  onClick={() => {
                    setSelectedEntry(null);
                    navigate(`/camdobrain/suggestions?brand_profile_id=${selectedBrandId}`);
                  }}
                >
                  Ver Sugerencia Completa
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CamDoBrainCalendar;
